import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Calendar, X } from 'lucide-react'
import { formatDateToMMDDYYYY, formatValueToMMDDYYYY, isValidMMDDYYYY, parseMMDDYYYYDate } from './customDateFormatUtils'

const CustomDatePickingFormat = ({ value, onChange, disabled = false, className = '', inputStyle = {}, placeholder = 'MM/DD/YYYY' }) => {
  const [inputValue, setInputValue] = useState(value || '')
  const [selectedDate, setSelectedDate] = useState(value && isValidMMDDYYYY(value) ? parseMMDDYYYYDate(value) : null)
  const [isOpen, setIsOpen] = useState(false)

  const [isFocused, setIsFocused] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 })

  const inputRef = useRef(null)
  const calendarRef = useRef(null)
  const calendarIconRef = useRef(null)

  const updateCalendarPosition = () => {
    if (!calendarIconRef.current) return

    const iconRect = calendarIconRef.current.getBoundingClientRect()
    const calendarWidth = calendarRef.current?.offsetWidth || 302
    const viewportPadding = 8
    const maxLeft = Math.max(viewportPadding, window.innerWidth - calendarWidth - viewportPadding)
    const left = Math.min(Math.max(iconRect.right - calendarWidth, viewportPadding), maxLeft)

    setCalendarPosition({
      top: iconRect.bottom + 8,
      left,
    })
  }

  const handleDateChange = (date) => {
    const formatted = formatDateToMMDDYYYY(date)
    setSelectedDate(date)
    setInputValue(formatted)
    setIsOpen(false)
    onChange?.(formatted)
  }

  const handleBlur = () => {
    const trimmed = inputValue.trim()
    if (!trimmed) {
      onChange?.('')
      return
    }

    if (!isValidMMDDYYYY(trimmed)) {
      alert('Please enter a valid date in MM/DD/YYYY format.')
      setInputValue('')
      setSelectedDate(null)
      onChange?.('')
    } else {
      const parsed = parseMMDDYYYYDate(trimmed)
      const formatted = formatDateToMMDDYYYY(parsed)
      setSelectedDate(parsed)
      setInputValue(formatted)
      onChange?.(formatted)
    }
  }

  const handleClear = () => {
    setInputValue('')
    setSelectedDate(null)
    setIsOpen(false)
    onChange?.('')
  }

  // const handleInputChange = (e) => {
  //   let val = e.target.value.replace(/[^\d]/g, '')

  //   if (val.length >= 3 && val.length <= 4) {
  //     val = `${val.slice(0, 2)}/${val.slice(2)}`
  //   } else if (val.length > 4) {
  //     val = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4, 8)}`
  //   }

  //   setInputValue(val)
  // }

  // solved:// when we dont focus outside it doesnt set the date
  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^\d]/g, '')

    if (val.length >= 3 && val.length <= 4) {
      val = `${val.slice(0, 2)}/${val.slice(2)}`
    } else if (val.length > 4) {
      val = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4, 8)}`
    }

    setInputValue(val)

    if (isValidMMDDYYYY(val)) {
      const parsed = parseMMDDYYYYDate(val)
      setSelectedDate(parsed)
      onChange?.(formatDateToMMDDYYYY(parsed))
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideInput = inputRef.current && inputRef.current.contains(e.target)
      const clickedInsideCalendar = calendarRef.current && calendarRef.current.contains(e.target)

      if (!clickedInsideInput && !clickedInsideCalendar) {
        setIsOpen(false)
        handleBlur()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [inputValue])

  useEffect(() => {
    if (!isOpen) return

    updateCalendarPosition()
    window.addEventListener('resize', updateCalendarPosition)
    window.addEventListener('scroll', updateCalendarPosition, true)

    return () => {
      window.removeEventListener('resize', updateCalendarPosition)
      window.removeEventListener('scroll', updateCalendarPosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (value && isValidMMDDYYYY(value)) {
      const parsed = parseMMDDYYYYDate(value)
      setSelectedDate(parsed)
      setInputValue(formatDateToMMDDYYYY(parsed))
    } else if (value) {
      const formattedValue = formatValueToMMDDYYYY(value)
      setSelectedDate(formattedValue ? parseMMDDYYYYDate(formattedValue) : null)
      setInputValue(formattedValue)
    } else if (!value) {
      setSelectedDate(null)
      setInputValue('')
    }
  }, [value])

  return (
    <div ref={inputRef} className="datepicker-wrapper datepicker-wrapper--dark" style={{ width: '100%' }}>
      <div className="position-relative" style={{ width: '100%' }}>
        <input
          className={`form-control css-13cymwt-control pe-5 ui-date-input ${className}`.trim()}
          style={inputStyle}
          value={inputValue}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(false)
            setIsFocused(true)
          }}
          onBlur={() => setIsFocused(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={disabled}
        />

        {inputValue && (
          <X
            size={16}
            style={{
              position: 'absolute',
              top: '50%',
              right: '36px',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
            }}
            className="ui-date-clear"
            onClick={handleClear}
            title="Clear date"
          />
        )}

        <Calendar
          ref={calendarIconRef}
          size={18}
          style={{
            position: 'absolute',
            top: '50%',
            right: '12px',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
          }}
          className="ui-date-calendar-icon"
          onClick={(e) => {
            e.stopPropagation()
            if (disabled) return
            updateCalendarPosition()
            setIsOpen((prev) => !prev)
          }}
        />

        {isOpen && createPortal(
          <div
            ref={calendarRef}
            className="ui-date-calendar-portal dashboard-dark-theme"
            style={{
              position: 'fixed',
              top: calendarPosition.top,
              left: calendarPosition.left,
              zIndex: 2000,
            }}
          >
            <DatePicker selected={selectedDate} onChange={handleDateChange} inline />
          </div>,
          document.body
        )}
      </div>
    </div>
  )
}

CustomDatePickingFormat.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  inputStyle: PropTypes.object,
  placeholder: PropTypes.string,
}

export default CustomDatePickingFormat
